import { FC, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { Column } from 'react-table';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { FaTrash } from 'react-icons/fa';

import API from 'core/Api';

import { useActiveClientContext } from 'store/active-client';
import { useLanguageContext } from 'store/language';
import { useModal } from 'store/modal';
import { getUserInfo } from 'utils/auth';
import { updateUserData } from 'utils/userDataSync';

import { formatDate } from 'utils/helpers/formatters';
import { buildClientUrl } from 'utils/helpers/client';
import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import FetchTable from 'components/table/FetchTable';
import { buildFilters } from 'components/table/utils/filtersConfig';
import styled from 'styled-components';

import InnerWrapper from '../InnerWrapper';

const HourlyAbsencesScreen: FC = () => {
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const { language } = useLanguageContext();
  const { showModal } = useModal();
  const history = useHistory();
  const queryClient = useQueryClient();

  const [loadingState, setLoadingState] = useState<{ [key: string]: boolean }>({});

  // Fetch and sync user info on mount
  const { data: userInfoData } = useQuery(['user-info', client], (): Promise<any> => {
    return updateUserData();
  });

  const userInfo = userInfoData || getUserInfo();

  const createHourlyAbsences = () => {
    const url = buildClientUrl('/my-hourly-absences/create', client);
    history.push(url);
  };

  const getTooltipText = (createdAt: string, updatedAt: string): string => {
    const createdDate = formatDate(new Date(createdAt), 'dd.mm.YYY hh:ii');
    const updatedDate = formatDate(new Date(updatedAt), 'dd.mm.YYY hh:ii');

    if (createdAt === updatedAt) {
      return `${t('hourlyAbsences.createdAt')}: ${createdDate}`;
    }

    return `${t('hourlyAbsences.createdAt')}: ${createdDate}\n${t('hourlyAbsences.updatedAt')}: ${updatedDate}`;
  };

  const {
    data: dropdowns,
    isLoading,
    error: dropdownError,
  } = useQuery(
    ['hourly-absences-dropdowns', client],
    (): Promise<{ [key: string]: any[] }> => {
      return API.get('hourly-absences/dropdown-options').then(res => {
        return res.data?.[language || 'bg'];
      });
    },
    { cacheTime: 0 }
  );

  const tableFilters = useMemo(
    () => [
      buildFilters({
        name: 'date_from',
        placeholder: t('hourlyAbsences.dateFrom'),
        type: 'monthyearpickerstart',
      }),
      buildFilters({
        name: 'date_to',
        placeholder: t('hourlyAbsences.dateTo'),
        type: 'monthyearpickerend',
      }),
      buildFilters({
        name: 'status',
        placeholder: t('hourlyAbsences.status'),
        options: dropdowns?.status,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
    ],
    [dropdowns]
  );

  const renderActions = (buttons: Record<string, any>, id: number) => {
    const handleDelete = (actionLink: string, text: string) => {
      showModal({
        type: 'confirm',
        heading: t('common.confirm'),
        content: text,
        onConfirm: async () => {
          setLoadingState(prev => ({ ...prev, [`delete-${id}`]: true }));
          try {
            await API.delete(actionLink);
            queryClient.invalidateQueries('hourly-absences');
            queryClient.invalidateQueries('user-info');
          } catch (error) {
            console.error('Failed to delete:', error);
          } finally {
            setLoadingState(prev => ({ ...prev, [`delete-${id}`]: false }));
          }
        },
      });
    };

    const buttonArray = Object.values(buttons);

    return buttonArray.map((button, index) => {
      const { type, text, action_link: actionLink } = button;

      if (type === 'delete') {
        const isButtonLoading = loadingState[`delete-${id}`];

        return (
          <div
            key={`delete-${id}`}
            className="d-inline-block ml-2"
          >
            <TooltipWrapper
              id={`tooltip-delete-${id}`}
              overlay={text}
            >
              <Button
                type="button"
                btnType="danger"
                size="sm"
                loading={isButtonLoading}
                disabled={isButtonLoading}
                onClick={() => handleDelete(actionLink, t('hourlyAbsences.confirmAbsence.delete'))}
              >
                <FaTrash size={14} />
              </Button>
            </TooltipWrapper>
          </div>
        );
      }

      return null;
    });
  };

  const columns: Array<Column<any>> = [
    {
      Header: t('hourlyAbsences.date'),
      accessor: 'date_from',
      width: '20%',
      disableSortBy: true,
      Cell: ({ row }) => {
        const { date_from: dateFrom, date_to: dateTo, created_at: createdAt, updated_at: updatedAt } = row.original;
        const formattedFrom = formatDate(new Date(dateFrom), 'dd.mm.yyy hh:ii');
        const formattedTo = formatDate(new Date(dateTo), 'hh:ii');
        const tooltip = getTooltipText(createdAt, updatedAt);
        return (
          <TooltipWrapper
            id={`tooltip-name-${row.original.id}`}
            overlay={tooltip}
          >
            {formattedFrom} - {formattedTo}
          </TooltipWrapper>
        );
      },
    },
    {
      Header: t('hourlyAbsences.hours'),
      accessor: 'hours',
      width: '12%',
      Cell: ({ cell }) => {
        const hours = Number(cell.value);
        const formattedHours = Number.isInteger(hours) ? hours : hours.toFixed(1);
        return <span>{formattedHours}</span>;
      },
    },
    {
      Header: t('hourlyAbsences.status'),
      width: '12%',
      accessor: 'status',
      Cell: ({ row }) => {
        const { status, created_at: createdAt, updated_at: updatedAt } = row.original;
        const tooltip = getTooltipText(createdAt, updatedAt);
        return (
          <TooltipWrapper
            id={`tooltip-name-${row.original.id}`}
            overlay={tooltip}
          >
            {t(`hourlyAbsences.statuses.${status}`)}
          </TooltipWrapper>
        );
      },
    },
    {
      Header: t('hourlyAbsences.leaveApplied'),
      accessor: 'hours_covered_total',
      width: '20%',
      Cell: ({ row }) => {
        const hoursRequested = Number(row.original.hours) || 0;
        const hoursCoveredTotal = Number(row.original.hours_covered_total) || 0;
        const hoursCovered = row.original.hours_covered;

        const formatNumber = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

        if (hoursCoveredTotal <= 0) {
          return <span>{t('hourlyAbsences.no')}</span>;
        }

        const dateFormatted =
          hoursCovered && hoursCovered.length > 0
            ? hoursCovered.map((hc: { date_formatted: string }) => hc.date_formatted).join(' / ')
            : '';

        if (hoursRequested > 0 && hoursCoveredTotal < hoursRequested) {
          return (
            <span>
              {t('hourlyAbsences.partialCovered', {
                covered: formatNumber(hoursCoveredTotal),
                total: formatNumber(hoursRequested),
              })}
              {dateFormatted ? ` (${dateFormatted})` : ''}
            </span>
          );
        }

        return (
          <span>
            {t('hourlyAbsences.yes')}
            {dateFormatted ? ` (${dateFormatted})` : ''}
          </span>
        );
      },
    },
    {
      Header: t('hourlyAbsences.comment'),
      accessor: 'comment',
      Cell: ({ cell }) => <span>{cell.value}</span>,
    },
    {
      Header: t('hourlyAbsences.actions'),
      accessor: 'actions',
      width: '8%',
      Cell: ({ row }) => {
        const { buttons, id } = row.original;
        return <div>{renderActions(buttons, id)}</div>;
      },
    },
  ];

  const formatHours = (hours: number | undefined): string => {
    if (hours === undefined || Number.isNaN(hours)) return '0';
    return Number.isInteger(hours) ? hours.toString() : hours.toFixed(1);
  };

  return (
    <InnerWrapper isLoading={isLoading}>
      {userInfo.total_hourly_absences_remaining_hours !== undefined && (
        <HourlyAbsencesInfoBanner>
          <InfoLabel>{t('hourlyAbsences.remainingHours')}:</InfoLabel>
          <InfoValue isNegative={userInfo.total_hourly_absences_remaining_hours < 0}>
            {formatHours(userInfo.total_hourly_absences_remaining_hours)} {t('hourlyAbsences.hoursLabel')}
          </InfoValue>
          {userInfo.total_hourly_absences_uncovered_hours !== undefined &&
            userInfo.total_hourly_absences_uncovered_hours > 0 && (
              <InfoHint>
                ({t('hourlyAbsences.uncoveredHours')}: {formatHours(userInfo.total_hourly_absences_uncovered_hours)}{' '}
                {t('hourlyAbsences.hoursLabel')})
              </InfoHint>
            )}
          {userInfo.total_hourly_absences_hours_about_to_be_covered !== undefined &&
            userInfo.total_hourly_absences_hours_about_to_be_covered > 0 && (
              <InfoHint>
                ({t('hourlyAbsences.hoursAboutToBeCovered')}:{' '}
                {formatHours(userInfo.total_hourly_absences_hours_about_to_be_covered)} {t('hourlyAbsences.hoursLabel')}
                )
              </InfoHint>
            )}
        </HourlyAbsencesInfoBanner>
      )}
      <FetchTable
        loading={isLoading}
        entityName="hourly-absences"
        entityEndpoint="hourly-absences"
        columns={columns}
        filters={tableFilters}
        hideSearch
        pageLimit={10}
        pagination
        onAdd={createHourlyAbsences}
        addBtnLabel={t('hourlyAbsences.requestMyHourlyAbsence')}
      />
    </InnerWrapper>
  );
};

export default HourlyAbsencesScreen;

const HourlyAbsencesInfoBanner = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: #495057;
  font-size: 14px;
`;

const InfoValue = styled.span<{ isNegative?: boolean }>`
  font-weight: 700;
  font-size: 16px;
  color: ${props => (props.isNegative ? '#dc3545' : '#28a745')};
`;

const InfoHint = styled.span`
  font-size: 13px;
  color: #6c757d;
  font-style: italic;
`;
